import { useContext, useState } from 'react';
import i18n from '@/utils/i18n';
import ClassicLoadingSpinner from '../components/LoadingSpinner/ClassicLoadingSpinner';

import { useGetContainerStationQuery } from '../graphql/queries/containerStation.generated';
import { useUpdateContainerStationMutation } from '../graphql/mutations/updateContainerStation.generated';
import { ContainerStationDetailFragment } from '../graphql/fragments/containerStationDetail.generated';
import {
  ContainerStationContainer,
  ItemTypeCategory,
} from '../graphql/generatedTypes';
import ItemLocationHistory from '../components/ItemLocationHistory';
import IndividualContainerStock from '../components/IndividualContainerStock';
import { CONTAINER_STATION_QR_CODE_PREFIX } from '../components/ContainerStationList';
import QRCodeStyling from 'qr-code-styling';
import { getActiveTabFromNav } from '@/utils/url';
import { ContentSection } from '../components/Layout/ContentSection';
import { MainHeader } from '../components/Layout/MainHeader';
import { FaCopy, FaSave } from 'react-icons/fa';
import { Button } from 'reactstrap';
import { LayoutStoreInstance } from '../LayoutStore';
import { ViewerContext } from '../providers/ViewerContextProvider';
import { ItemTypeSelector } from '../components/ItemTypeSelector';
import TrackableItemDisplay from '../components/TrackableItem/TrackableItemDisplay';
import { Role } from '../contants';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

export type ContainerStationWithQrCode = ContainerStationDetailFragment & {
  qrCode: string;
};

export const PAGE_SIZE = 50;

export type ContainerPaginationResult = {
  total: number;
  containers: ContainerStationContainer[];
};

export const ContainerStationDetailView = function ContainerStationDetailView({
  match: {
    params: { stationId },
  },
}: {
  match: {
    params: { stationId: string | null };
  };
}) {
  const context = useContext(ViewerContext);
  const readOnly = context.role === Role.eventCleaningEmployee;
  const [containerStation, setContainerStation] =
    useState<ContainerStationWithQrCode>();

  useDocumentTitle('PageTitles:containerStation', containerStation?.name);

  const { loading } = useGetContainerStationQuery({
    skip: stationId == null,
    variables: { id: stationId || '' },
    onCompleted: async (d) => {
      const withQrCode = await addStationQrCodes(d.containerStation);
      setContainerStation(withQrCode);
    },
  });

  async function addStationQrCodes(
    containerStation: ContainerStationDetailFragment,
  ) {
    const stationWithQrCode = {
      //@ts-ignore
      qrCode: null as string,
      ...containerStation,
    };
    if (stationWithQrCode.qrCode == null) {
      stationWithQrCode.qrCode = await getContainerStationQRCode(
        stationWithQrCode.id,
      );
    }
    return stationWithQrCode;
  }

  async function getContainerStationQRCode(id: string) {
    const qrCode = getQrCodeStyling(id);
    const blob: any = await qrCode.getRawData('png');
    return window.URL.createObjectURL(blob);
  }

  function getQrCodeStyling(id: string) {
    return new QRCodeStyling({
      width: 100,
      data: `${CONTAINER_STATION_QR_CODE_PREFIX}${id}`,
      height: 100,
      dotsOptions: {
        color: '#000000',
        type: 'square',
      },
      imageOptions: {
        crossOrigin: 'anonymous',
        margin: 10,
      },
      backgroundOptions: {
        color: '#ffffff',
      },
      qrOptions: {
        errorCorrectionLevel: 'Q',
      },
    });
  }

  const [updateContainerStation, { loading: updating }] =
    useUpdateContainerStationMutation({
      onCompleted: async (d) => {
        const withQrCode = await addStationQrCodes(d.updateContainerStation);
        setContainerStation(withQrCode);
      },
    });

  async function save() {
    if (containerStation != null) {
      await updateContainerStation({
        variables: {
          id: containerStation.id,
          name: containerStation.name,
          type_id: containerStation.item_type.id,
          is_recalled: containerStation.is_recalled,
        },
      });
    }
  }

  if (loading || containerStation?.id == null) {
    return <ClassicLoadingSpinner />;
  }

  const activeTabString = getActiveTabFromNav();
  const activeTab = activeTabString?.split('_')[0];

  return (
    <div>
      <MainHeader title={i18n.t('ContainerStation:containerStation')} />
      <ContentSection
        title={i18n.t('ContainerStation:generalInfo')}
        collapsable={false}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ width: '66%' }}>
            <div className="input-group mb-3">
              <div className="input-group-prepend">
                <span className="input-group-text" id="basic-addon1">
                  {i18n.t('Common:name')}
                </span>
              </div>
              <input
                className="form-control"
                type="text"
                disabled={readOnly}
                onChange={(e) =>
                  setContainerStation({
                    ...containerStation,
                    name: e.target.value,
                  })
                }
                value={containerStation.name}
              />
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(containerStation.name);
                  LayoutStoreInstance.setSuccess(i18n.t('Common:copied'));
                }}
              >
                <FaCopy />
              </Button>
            </div>
            <div className="input-group mb-3">
              <div className="input-group-prepend">
                <span className="input-group-text">{i18n.t('Common:id')}</span>
              </div>
              <input
                className="form-control"
                type="text"
                disabled
                value={containerStation.id}
              />
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(containerStation.id);
                  LayoutStoreInstance.setSuccess(i18n.t('Common:copied'));
                }}
              >
                <FaCopy />
              </Button>
            </div>
            <div className="input-group mb-3">
              <div className="input-group-prepend">
                <span className="input-group-text">
                  {i18n.t('Common:type')}
                </span>
              </div>
              <ItemTypeSelector
                category={ItemTypeCategory.ContainerStation}
                itemTypeId={containerStation.item_type.id.toString()}
                setItemTypeId={(value) => {
                  setContainerStation({
                    ...containerStation,
                    item_type: {
                      ...containerStation.item_type,
                      id: parseInt(value, 10),
                    },
                  });
                }}
                disabled={readOnly}
                className="form-control"
              />
            </div>
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('ContainerStation:storeName')}
              </span>
              <input
                className="form-control"
                type="text"
                value={containerStation.store.name || ''}
                disabled
              />
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(
                    containerStation.store.name || '',
                  );
                  LayoutStoreInstance.setSuccess(i18n.t('Common:copied'));
                }}
              >
                <FaCopy />
              </Button>
            </div>
            <div className="form-check mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="isRecalledCheckbox"
                checked={containerStation.is_recalled}
                disabled={readOnly}
                onChange={(e) =>
                  setContainerStation({
                    ...containerStation,
                    is_recalled: e.target.checked,
                  })
                }
              />
              <label className="form-check-label" htmlFor="isRecalledCheckbox">
                {i18n.t('ContainerStation:isRecalled')}
              </label>
            </div>
          </div>
          <div className="me-2 mt-2 mb-2">
            <div className="container-image-container-xl mb-3 bg-white p-1">
              <img alt="QR code" src={containerStation.qrCode}></img>
            </div>
            <TrackableItemDisplay
              trackableItem={containerStation}
              showTrackableItemId={false}
              showLink={false}
              showName={false}
              size="xl"
            />
          </div>
        </div>
        <Button className="float-end mt-2" onClick={save} disabled={readOnly}>
          <FaSave className="me-1" />
          {i18n.t('Common:save')}
        </Button>
      </ContentSection>
      <ItemLocationHistory itemId={containerStation.id} />
      <IndividualContainerStock stationId={containerStation.id} />
    </div>
  );
};
